import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { competitionAverageSelectedCompetitionsAtom } from '@/atoms/filters/player/playerCompetitionAverageFilters';
import { unwrappedSelectedPlayerNameAtom } from '@/atoms/player/info';
import { playerIdAtom } from '@/atoms/player/player';
import { unwrappedPlayerComparisonDisplayNameAtom } from '@/atoms/queries/player/playerComparisonInfo';
import { playerRenderedComparisonSourceAtom } from '@/atoms/queries/player/playerLeagueAverageRadar';
import {
  renderedRadarDataAtom,
  unwrappedRadarDataFormatAtom,
  unwrappedRadarTableComparisonPercentilesAtom,
  unwrappedRadarTableMetricPercentilesAtom,
} from '@/atoms/radar';
import { RadarComparisonLegend, RadarLegendItem } from '@/components/radar/RadarComparisonLegend';
import { useIsCompetitionAverageMode } from '@/hooks/useIsCompetitionAverageMode';
import { RadarDataFormat } from '@/types/radar';
import { toPercentileRadarData, toRadarVisData } from '@/utils/radar';

export const PlayerComparisonLegend = ({ id }: { id: string }) => {
  const { t } = useTranslation(['metrics', 'radar']);
  const playerId = useAtomValue(playerIdAtom);
  const playerName = useAtomValue(unwrappedSelectedPlayerNameAtom);
  const renderedRadarData = useAtomValue(renderedRadarDataAtom);
  const entityComparisonName = useAtomValue(unwrappedPlayerComparisonDisplayNameAtom);
  const { data: renderedComparisonData } = useAtomValue(playerRenderedComparisonSourceAtom);
  const metricPercentiles = useAtomValue(unwrappedRadarTableMetricPercentilesAtom);
  const comparisonPercentiles = useAtomValue(unwrappedRadarTableComparisonPercentilesAtom);
  const radarDataFormat = useAtomValue(unwrappedRadarDataFormatAtom);
  const isPercentileMode = radarDataFormat === RadarDataFormat.Percentiles;
  const isCompetitionAverageMode = useIsCompetitionAverageMode();
  const competitionAverageCompetitions = useAtomValue(competitionAverageSelectedCompetitionsAtom);

  const competitionAverageName =
    competitionAverageCompetitions[0]?.label ?? t('competitionAverage.optionLabel', { ns: 'radar' });
  const comparisonName = isCompetitionAverageMode ? competitionAverageName : entityComparisonName;

  const primaryData = isPercentileMode
    ? toPercentileRadarData(renderedRadarData, metricPercentiles)
    : renderedRadarData;
  const comparisonData = isPercentileMode
    ? toPercentileRadarData(renderedComparisonData, comparisonPercentiles)
    : renderedComparisonData;

  const radars: [RadarLegendItem, RadarLegendItem] = [
    { id: `player-${playerId}-${id}`, name: playerName, data: toRadarVisData(t, primaryData) },
    { id: `comparison-player-${id}`, name: comparisonName, data: toRadarVisData(t, comparisonData) },
  ];

  return <RadarComparisonLegend radars={radars} />;
};
